import {useSyncExternalStore} from 'react';

/**
 * Viewport helpers for the mobile-adaptive layer.
 *
 * Widths mirror `src/constants/breakpoints.less` and Ant Design's Grid, so LESS media
 * queries, these hooks and Pro Layout's internal `isMobile` all flip at the same width.
 */
export const MOBILE_MAX_WIDTH = 767;
export const TABLET_MAX_WIDTH = 991;

const MOBILE_QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`;
const TABLET_QUERY = `(max-width: ${TABLET_MAX_WIDTH}px)`;

/**
 * One subscribe function per query, so React doesn't resubscribe on every render.
 * `window.matchMedia` is called inside, never at module load: the tests swap the global
 * in `beforeEach`, and a captured MediaQueryList would outlive the mock that made it.
 */
const subscribers = new Map<string, (onStoreChange: () => void) => () => void>();

const getSubscribe = (query: string) => {
  const cached = subscribers.get(query);
  if (cached) return cached;

  const subscribe = (onStoreChange: () => void) => {
    const mediaQueryList = window.matchMedia(query);
    mediaQueryList.addEventListener('change', onStoreChange);
    // Belt and braces: a missed `change` used to strand one component on the wrong
    // branch while the rest of the page rendered desktop (see docs/responsive.md).
    // `resize` re-reads the same source, and an unchanged snapshot costs no render.
    window.addEventListener('resize', onStoreChange);
    return () => {
      mediaQueryList.removeEventListener('change', onStoreChange);
      window.removeEventListener('resize', onStoreChange);
    };
  };

  subscribers.set(query, subscribe);
  return subscribe;
};

// No `getServerSnapshot` on purpose. Umi renders client-side, but passing one makes React
// use it for the first render of a hydrating root and then keep that value until the store
// notifies — so a phone that never resizes would stay latched on the desktop branch.
const useMediaQuery = (query: string): boolean =>
  useSyncExternalStore(getSubscribe(query), () => window.matchMedia(query).matches);

/** True on phone-sized viewports — the switch for bottom sheets, card lists and the tab bar. */
export const useIsMobile = (): boolean => useMediaQuery(MOBILE_QUERY);

/** True on phones and small tablets, where desktop-density layouts still do not fit. */
export const useIsCompact = (): boolean => useMediaQuery(TABLET_QUERY);

export default useIsMobile;
